import { Back, Power3 } from 'gsap';
import Rectangle from '../../graphics/Rectangle';
import Text from '../../graphics/Text';
import DraggablePreset from './DraggablePreset';
import {
  getCenter,
  getCenterX,
  getCenterY,
  getFixRightX,
} from '../../utils/CoordinateCalculator';

const MAX_DURATION = 3;

export default class PopText extends DraggablePreset {
  constructor(app) {
    super(app);
    this.text = new Text(app);
    this.backgroundBox = new Rectangle();
    this.foregroundBox = new Rectangle();

    this.container.addChild(this.text);
  }

  update(model) {
    super.update(model);

    const totalDuration = Math.min(MAX_DURATION, this.duration);
    const expandDuration = totalDuration / 4;
    const expandDelay = totalDuration / 6;
    const popDuration = totalDuration / 10;
    const popDelay = totalDuration / 60;

    this.text.text = model.text;
    this.text.updateStyle(model);

    const scale = this.text.fontSize;

    /**
     * Setup our root objects that everything else will be
     * positioned and sized off of.
     */
    this.popDistance = scale * 0.35;
    this.popDirection = this.align === 'right' ? 'left' : 'right';

    this.setDraggableRoot(
      this.text.lineControls.height + scale * 0.5,
      this.text.lineControls.width + scale * 0.85
    );

    /**
     * Position the initial elements based off of the root
     * and set them to their starting values.
     */
    this.text.lineControls.x = this.root.x;
    this.text.lineControls.y = getCenterY(this.text.lineControls, this.root);
    this.text.alpha = 0;

    const rootBox = new Rectangle(0, 0, this.root.width, this.root.height);
    rootBox.x = getCenterX(rootBox, this.root);
    rootBox.y = getCenterY(rootBox, this.root);
    rootBox.width = 0;

    this.foregroundBox = rootBox.clone();
    this.foregroundBox.color = this.secondaryColor;

    this.backgroundBox = rootBox.clone();
    this.backgroundBox.color = this.primaryColor;

    // Create some reusable changes
    const expand = {
      width: this.root.width,
      duration: expandDuration,
      ease: Power3.easeOut,
    };

    const popUp = {
      ...this.pop(rootBox),
      duration: popDuration,
      ease: Back.easeInOut,
    };

    const slideOut = {
      width: 0,
      x: this.popX(rootBox.x + this.root.width),
      duration: expandDuration,
      ease: Power3.easeIn,
    };

    // Create the timelines
    this.animate(this.backgroundBox)
      .to(expand)
      .to(
        { ...popUp, ease: Back.easeIn },
        `end-=${expandDuration + expandDelay + popDelay + popDuration}`
      )
      .to(slideOut, `end-=${expandDuration}`);

    this.animate(this.foregroundBox)
      .to(expand, expandDelay)
      .to(popUp, expandDelay + expandDuration + popDelay)
      .to(slideOut, `end-=${expandDuration + expandDelay}`);

    this.animate(this.text.lineControls)
      .to(
        {
          alpha: 1,
          x: getCenterX(this.text.lineControls, this.root),
          duration: expandDelay,
        },
        expandDelay
      )
      .to(
        {
          ...this.pop(getCenter(this.text.lineControls, this.root)),
          ease: Back.easeInOut,
          duration: popDuration,
        },
        expandDelay + expandDuration + popDelay
      )
      .to(
        {
          alpha: 0,
          x: this.popX(getFixRightX(this.text.lineControls, this.root)),
          duration: popDuration,
        },
        `end-=${expandDelay * 2}`
      );
  }

  pop(item) {
    return {
      x: this.popX(item.x),
      y: this.popY(item.y),
    };
  }

  popY(y) {
    return y - this.popDistance;
  }

  popX(x) {
    return (
      x + (this.popDirection === 'right' ? this.popDistance : -this.popDistance)
    );
  }

  render() {
    super.render();
    this.backgroundBox.draw(this.graphics);
    this.foregroundBox.draw(this.graphics);
  }
}
